import { addMonths, isAfter, isBefore, startOfDay, startOfMonth, subMonths } from 'app/lib/date';
import { useState } from 'react';
import Animated, { LinearTransition } from 'react-native-reanimated';
import { Column } from '../box';
import { CalendarControls } from './controls';
import type { CalendarMonthProps } from './month';
import { CalendarMonth } from './month';

interface CalendarProps extends Omit<CalendarMonthProps, 'current'> {
  disabled?: boolean;
  initial?: Date | null;
  maxCurrent?: Date;
  minCurrent?: Date;
  startFromNow?: boolean;
}

export function Calendar({
  disabled,
  initial,
  maxCurrent,
  maxDate,
  minCurrent,
  minDate,
  startFromNow,
  ...monthProps
}: CalendarProps) {
  const today = startOfDay(new Date());

  const [current, setCurrent] = useState(initial ?? today);

  const minimumNavigationDate = minCurrent ?? minDate;
  const maximumNavigationDate = maxCurrent ?? maxDate;
  const minCurrentMonth = minimumNavigationDate ? startOfMonth(minimumNavigationDate) : null;
  const maxCurrentMonth = maximumNavigationDate ? startOfMonth(maximumNavigationDate) : null;
  const currentMonth = startOfMonth(current);

  const disablePrevious = minCurrentMonth ? !isAfter(currentMonth, minCurrentMonth) : false;
  const disableNext = maxCurrentMonth ? !isBefore(currentMonth, maxCurrentMonth) : false;

  const onPreviousPress = () => {
    if (disablePrevious) return;
    setCurrent((prev) => subMonths(prev, 1));
  };

  const onNextPress = () => {
    if (disableNext) return;
    setCurrent((prev) => addMonths(prev, 1));
  };

  return (
    <Column
      backgroundColor="surface.default"
      borderColor="border.secondary"
      borderRadius="radius.xl"
      borderWidth={1}
      gap="spacing.general.x3"
      padding="spacing.general.x3"
      style={{ pointerEvents: disabled ? 'none' : 'auto' }}
    >
      <CalendarControls
        {...{ current, onNextPress, onPreviousPress }}
        disableNext={disableNext}
        disablePrevious={disablePrevious}
      />
      <Animated.View layout={LinearTransition.duration(300)}>
        <CalendarMonth
          {...{ current }}
          {...monthProps}
          disabled={disabled}
          minCurrent={minCurrent}
          maxCurrent={maxCurrent}
          minDate={minDate}
          maxDate={maxDate}
          startFromNow={startFromNow}
        />
      </Animated.View>
    </Column>
  );
}
